import {
  FilterList,
  Event,
  Work,
  Settings,
  Task,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { TimelineFilters } from "../../types/Timeline.types";

interface TimelineFiltersProps {
  filters: TimelineFilters;
  onFiltersChange: (filters: TimelineFilters) => void;
  itemCounts: {
    eventData: number;
    works: number;
    eventLogs: number;
    workItems: number;
  };
}

const TimelineFiltersComponent = ({
  filters,
  onFiltersChange,
  itemCounts,
}: TimelineFiltersProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleFilterChange = (filterKey: keyof TimelineFilters, value: boolean) => {
    onFiltersChange({
      ...filters,
      [filterKey]: value,
    });
  };

  const getActiveFiltersCount = () => {
    return [
      filters.showEventData,
      filters.showWorks,
      filters.showEventLogs,
      filters.showWorkItems,
    ].filter(Boolean).length;
  };

  const resetFilters = () => {
    onFiltersChange({
      showEventData: true,
      showWorks: true,
      showEventLogs: true,
      showWorkItems: true,
    });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList color="action" />
            <Typography variant="h6" fontWeight="600">
              Filter
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({getActiveFiltersCount()} av 4 aktiva)
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              variant="text"
              onClick={resetFilters}
              sx={{ fontSize: "0.75rem" }}
            >
              Återställ
            </Button>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <FilterList />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box mt={2}>
            <FormGroup>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.showEventData}
                      onChange={(e) =>
                        handleFilterChange("showEventData", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Event fontSize="small" color="primary" />
                      <Typography variant="body2">
                        Händelser ({itemCounts.eventData})
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.showWorks}
                      onChange={(e) =>
                        handleFilterChange("showWorks", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Work fontSize="small" color="success" />
                      <Typography variant="body2">
                        Ärenden ({itemCounts.works})
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.showEventLogs}
                      onChange={(e) =>
                        handleFilterChange("showEventLogs", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Settings fontSize="small" color="info" />
                      <Typography variant="body2">
                        Systemloggar ({itemCounts.eventLogs})
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.showWorkItems}
                      onChange={(e) =>
                        handleFilterChange("showWorkItems", e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Task fontSize="small" color="warning" />
                      <Typography variant="body2">
                        Aktiviteter ({itemCounts.workItems})
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </FormGroup>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TimelineFiltersComponent;